// Módulo 3.2.4: Formularios Inteligentes
// Sistema de formularios avanzados con validación inteligente, autocompletado y manejo de archivos

import type { App } from 'vue'
import { defineAsyncComponent, ref, readonly } from 'vue'

// Importaciones para el plugin
import FormValidation from './FormValidation.vue'
import SmartIncidentForm from './SmartIncidentForm.vue'
import UserFormDialog from './UserFormDialog.vue'
import InventoryRequestForm from './InventoryRequestForm.vue'

// Componentes principales exportados
export { default as FormValidation } from './FormValidation.vue'
export { default as SmartIncidentForm } from './SmartIncidentForm.vue'
export { default as UserFormDialog } from './UserFormDialog.vue'
export { default as InventoryRequestForm } from './InventoryRequestForm.vue'

// Componentes optimizados con lazy loading
const OptimizedFormsDemo = defineAsyncComponent(() => import('@/views/OptimizedFormsDemo.vue'))

// Plugin para registro en Vue
export default {
  install(app: App) {
    // Registrar componente demo optimizado
    app.component('OptimizedFormsDemo', OptimizedFormsDemo)
    
    // Registrar todos los componentes de formularios
    app.component('FormValidation', FormValidation)
    app.component('SmartIncidentForm', SmartIncidentForm)
    app.component('UserFormDialog', UserFormDialog)
    app.component('InventoryRequestForm', InventoryRequestForm)
    
    console.log('✅ Componentes de formularios optimizados registrados')
  }
}

// Tipos y interfaces para los formularios
export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  email?: boolean
  custom?: (value: any) => boolean
  message: string
}

export interface FormField {
  name: string
  label: string
  type: 'text' | 'email' | 'password' | 'number' | 'date' | 'select' | 'textarea' | 'checkbox' | 'file'
  required?: boolean
  placeholder?: string
  options?: Array<{ title: string; value: any }>
  rules?: ValidationRule[]
}

export interface FormConfig {
  fields: FormField[]
  validationStrategy?: 'onSubmit' | 'onChange' | 'onBlur'
  showValidationSummary?: boolean
  autoSave?: boolean
  autoSaveInterval?: number
}

// Utilidades para formularios
export const FormUtils = {
  /**
   * Valida un email
   */
  validateEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  },

  /**
   * Valida una contraseña segura
   */
  validatePassword: (password: string): boolean => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
    return passwordRegex.test(password)
  },

  /**
   * Valida un número de teléfono
   */
  validatePhone: (phone: string): boolean => {
    const phoneRegex = /^[\d\-\+\(\)\s]+$/
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 7
  },

  /**
   * Formatea el tamaño de un archivo
   */
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  },

  /**
   * Obtiene el icono para un tipo de archivo
   */
  getFileIcon: (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'mdi-image'
    if (mimeType.includes('pdf')) return 'mdi-file-pdf-box'
    if (mimeType.includes('word')) return 'mdi-file-word-box'
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'mdi-file-excel-box'
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'mdi-file-powerpoint-box'
    if (mimeType.includes('zip') || mimeType.includes('rar')) return 'mdi-folder-zip'
    return 'mdi-file'
  },

  /**
   * Debounce function para validación en tiempo real
   */
  debounce: <T extends (...args: any[]) => any>(func: T, wait: number): T => {
    let timeout: NodeJS.Timeout
    return ((...args: any[]) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(null, args), wait)
    }) as T
  },

  /**
   * Genera un ID único para formularios
   */
  generateFormId: (): string => {
    return `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  },

  /**
   * Convierte FormData a objeto plano
   */
  formDataToObject: (formData: FormData): Record<string, any> => {
    const obj: Record<string, any> = {}
    for (const [key, value] of formData.entries()) {
      if (obj[key]) {
        if (Array.isArray(obj[key])) {
          obj[key].push(value)
        } else {
          obj[key] = [obj[key], value]
        }
      } else {
        obj[key] = value
      }
    }
    return obj
  }
}

// Constantes para configuración de formularios
export const FORM_CONSTANTS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_DOCUMENT_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ],
  AUTO_SAVE_INTERVAL: 30000, // 30 segundos
  VALIDATION_DEBOUNCE_TIME: 300, // 300ms
  DEFAULT_VALIDATION_MESSAGES: {
    required: 'Este campo es requerido',
    email: 'Ingresa un email válido',
    minLength: 'Mínimo {min} caracteres',
    maxLength: 'Máximo {max} caracteres',
    pattern: 'Formato inválido',
    custom: 'Valor inválido'
  }
}

// Hooks de Vue para formularios
export const useFormValidation = () => {
  const errors = ref<Record<string, string>>({})
  const touched = ref<Record<string, boolean>>({})
  const isValid = ref(false)

  const validateField = (name: string, value: any, rules: ValidationRule[] = []) => {
    const fieldErrors: string[] = []

    for (const rule of rules) {
      if (rule.required && (!value || value.toString().trim() === '')) {
        fieldErrors.push(rule.message || FORM_CONSTANTS.DEFAULT_VALIDATION_MESSAGES.required)
        continue
      }

      if (value && rule.minLength && value.toString().length < rule.minLength) {
        fieldErrors.push(rule.message || FORM_CONSTANTS.DEFAULT_VALIDATION_MESSAGES.minLength.replace('{min}', rule.minLength.toString()))
        continue
      }

      if (value && rule.maxLength && value.toString().length > rule.maxLength) {
        fieldErrors.push(rule.message || FORM_CONSTANTS.DEFAULT_VALIDATION_MESSAGES.maxLength.replace('{max}', rule.maxLength.toString()))
        continue
      }

      if (value && rule.email && !FormUtils.validateEmail(value)) {
        fieldErrors.push(rule.message || FORM_CONSTANTS.DEFAULT_VALIDATION_MESSAGES.email)
        continue
      }

      if (value && rule.pattern && !rule.pattern.test(value)) {
        fieldErrors.push(rule.message || FORM_CONSTANTS.DEFAULT_VALIDATION_MESSAGES.pattern)
        continue
      }

      if (value && rule.custom && !rule.custom(value)) {
        fieldErrors.push(rule.message || FORM_CONSTANTS.DEFAULT_VALIDATION_MESSAGES.custom)
        continue
      }
    }

    if (fieldErrors.length > 0) {
      errors.value[name] = fieldErrors[0]
    } else {
      delete errors.value[name]
    }

    return fieldErrors.length === 0
  }

  const markFieldAsTouched = (name: string) => {
    touched.value[name] = true
  }

  const validateForm = (formData: Record<string, any>, rules: Record<string, ValidationRule[]>) => {
    let formIsValid = true

    for (const [fieldName, fieldRules] of Object.entries(rules)) {
      const fieldValue = formData[fieldName]
      const fieldIsValid = validateField(fieldName, fieldValue, fieldRules)
      markFieldAsTouched(fieldName)
      
      if (!fieldIsValid) {
        formIsValid = false
      }
    }

    isValid.value = formIsValid
    return formIsValid
  }

  const clearErrors = () => {
    errors.value = {}
    touched.value = {}
    isValid.value = false
  }

  return {
    errors: readonly(errors),
    touched: readonly(touched),
    isValid: readonly(isValid),
    validateField,
    validateForm,
    markFieldAsTouched,
    clearErrors
  }
}
